import { NavLink, useNavigate } from 'react-router';
import { CSSProperties, ReactNode } from 'react';
import { useLocation } from 'react-router';

export type NavLinkAdapterPropsType = {
	activeClassName?: string;
	activeStyle?: CSSProperties;
	children?: ReactNode;
	to?: string;
	href?: string;
	className?: string;
	style?: CSSProperties;
	role?: string;
	exact?: boolean;
	ref?: React.RefObject<HTMLAnchorElement>;
	end?: boolean;
};

/**
 * The NavLinkAdapter component is a wrapper around the NavLink component.
 * It adds the ability to navigate programmatically using the useRouter hook.
 * For URLs with query strings, it uses exact matching instead of React Router's partial matching.
 */
function NavLinkAdapter(props: NavLinkAdapterPropsType) {
	const { children, activeClassName = 'active', activeStyle, role = 'button', to, href, ref, exact, end, ..._props } = props;
	const location = useLocation();
	const navigate = useNavigate();

	const targetUrl = to || href;

	// Check if the URL has a query string - if so, use exact matching
	const hasQueryString = targetUrl && targetUrl.includes('?');
	
	// For exact query string matching, compare the full location string
	const isActive = hasQueryString 
		? (location.pathname + location.search === targetUrl || location.pathname === targetUrl)
		: false;

	// If it's a query string URL, render as a regular link with manual active state
	if (hasQueryString) {
		const combinedClassName = [
			_props.className,
			isActive ? activeClassName : null
		].filter(Boolean).join(' ');

		return (
			<a
				role={role}
				href={targetUrl}
				className={combinedClassName}
				style={{
					..._props.style,
					...(isActive ? activeStyle : null)
				}}
				ref={ref}
				onClick={(e) => {
					e.preventDefault();
					// Use React Router navigate for client-side navigation
					navigate(targetUrl);
				}}
			>
				{children}
			</a>
		);
	}

	// For regular URLs without query strings, use NavLink
	return (
		<NavLink
			role={role}
			to={targetUrl}
			end={end}
			className={({ isActive: navLinkIsActive }) =>
				[_props.className, navLinkIsActive ? activeClassName : null].filter(Boolean).join(' ')
			}
			style={({ isActive: navLinkIsActive }) => ({
				..._props.style,
				...(navLinkIsActive ? activeStyle : null)
			})}
			ref={ref}
			{..._props}
		>
			{children}
		</NavLink>
	);
}

export default NavLinkAdapter;
